import { ENTITLEMENT_LIST_SORT_ACTION_MAPPING } from '../../../../../../shared/constants/entitlements';
import { type ListTrackerState } from '../../../../../../shared/types';
import {
  type EntitlementPagination,
  type EntitlementSortState,
} from '../../../../../ViolationsEntitlement/data-layer/entitlementTypes';
import { type EntitlementRisksSidePanelRowContextState } from '../../../../data-layer/detailsTypes';
import type { RiskItem } from '../data-layer/inherentRisksTypes';

/**
 * Inherent Risks List Utility Functions
 *
 * Collection of utility functions for processing and transforming inherent risks
 * data for API calls, UI display, and state management. Provides centralized
 * data transformation logic for the inherent risks feature.
 *
 * Key Functions:
 * - API payload construction for inherent risks list queries
 * - Filter data transformation and mapping
 * - Row tracker state configuration for UI interactions
 * - Side panel context data extraction
 * - Unique value extraction for filter options
 * - Sort configuration and mapping
 *
 * Data Transformations:
 * - Converts filter states to API-compatible formats
 * - Maps UI sort options to backend sort parameters
 * - Extracts and formats data for side panel display
 * - Configures tracking metadata for list row interactions
 *
 * @fileoverview Utility functions for inherent risks list data processing
 */

type FilterMapValueType = string[] | undefined;

/**
 * Creates filter payload for inherent risks list API
 * Transforms filter map to structured API format
 */
const getFilterPayload = (filterMap: Record<string, FilterMapValueType>) => ({
  businessProcesses: filterMap.businessProcess ?? [],
  rulesets: filterMap.ruleset ?? [],
  riskNames: filterMap.riskName ?? [],
  riskTypes: filterMap.riskType ?? [],
  statuses: filterMap.status ?? [],
  priorities: filterMap.priority ?? [],
  functionKeys: (filterMap.functionName ?? []).map(Number),
});

/**
 * Creates the complete payload for inherent risks list API call
 * Combines filters, pagination, and sorting parameters
 */
export const getInherentRisksListPayload = (
  filterMap: Record<string, FilterMapValueType>,
  sortInAction?: EntitlementSortState,
  pagination: EntitlementPagination = { max: 10, offset: 0 }
) => {
  const filters = getFilterPayload(filterMap);

  return {
    pagination: {
      offset: pagination.offset,
      max: pagination.max,
    },
    sort: {
      field: sortInAction?.value ?? 'riskName',
      order: sortInAction?.order
        ? ENTITLEMENT_LIST_SORT_ACTION_MAPPING[sortInAction.order]
        : 'DESC',
    },
    filters,
  };
};

/**
 * Configures tracker state for list rows
 * Adds UI interaction metadata to risk data
 */
export const configureRisksTracker = (
  listData: RiskItem[],
  isChecked = false
): (RiskItem & {
  tracker: ListTrackerState;
})[] =>
  listData.map((listRow, index) => ({
    ...listRow,
    tracker: {
      id: listRow.riskId.toString(),
      isChecked,
      isRowDetailsOpen: false,
      isTransition: false,
      isFirstAddded: index === 0,
      isReadOnlyAction: false,
    },
  }));

/**
 * Gets row context for side panel
 * Extracts relevant data for detailed view
 */
export const getRiskRowContext = (
  row: RiskItem
): EntitlementRisksSidePanelRowContextState => ({
  riskId: row.riskId,
  riskScore: row.riskScore ?? 0,
  endpointName: row.endpointName ?? '',
  riskOwner: row.riskOwner ?? '',
  riskName: row.riskName ?? '',
  riskViolationSummaryObjects: {
    riskScore: row.riskViolationSummaryObjects?.riskScore ?? 0,
    violationEntitlementCount:
      row.riskViolationSummaryObjects?.violationEntitlementCount ?? 0,
    violationCount: row.riskViolationSummaryObjects?.violationCount ?? 0,
  },
  description: row.description ?? '',
  priority: row.priority ?? '',
  riskType: row.riskType ?? '',
  ruleset: row.ruleset ?? '',
  function1: row.function1 ?? '',
  function2: row.function2 ?? '',
  function3: row.function3 ?? '',
  function4: row.function4 ?? '',
  function5: row.function5 ?? '',
  businessProcess: row.businessProcess ?? null,
  customProperty1: row.customProperty1 ?? null,
  customProperty2: row.customProperty2 ?? null,
});

/**
 * Gets unique label entries for filter options
 * Creates filter options from risk data
 */
export const getUniqueLabelEntries = (
  data: RiskItem[],
  key: keyof RiskItem
) => {
  const uniqueValues = [...new Set(data.map((item) => item[key]))];

  return uniqueValues
    .filter(Boolean) // Removes null, undefined, false, 0, '', etc.
    .filter((value) => (value !== null ? String(value).trim() : false)) // Removes whitespace-only strings
    .map((value) => ({
      label: String(value),
      value: String(value),
      isChecked: false,
    }));
};
